import { useCallback, useLayoutEffect, useState } from 'react';
import { localPhase } from './lib/daylight';

// Three art directions share one layout. `hero.screen` holds the corners
// (TL, TR, BL, BR in source-image pixels) of the tablet screen the hero dives
// into; themes without one float the product panel up instead, zooming
// toward `hero.target`. `focus` works like object-position.
export const THEMES = {
  dawn: {
    key: 'dawn',
    label: 'Dawn',
    swatch: ['#f2c3a1', '#5468f0'],
    themeColor: '#101119',
    hero: {
      name: 'hero-dawn',
      w: 3072,
      h: 1728,
      focus: [0.511, 0.62],
      screen: [
        [1210, 1028],
        [1930, 1028],
        [1150, 1512],
        [1995, 1515],
      ],
    },
    cta: { name: 'cta-dawn', position: '50% 40%' },
    cards: ['walkaround-dusk', 'hangar-night', 'throttle-dusk'],
  },
  night: {
    key: 'night',
    label: 'Night',
    swatch: ['#0b1220', '#3cc8f4'],
    themeColor: '#05080f',
    hero: { name: 'hero-night', w: 3072, h: 1728, focus: [0.36, 0.72], target: [0.34, 0.58] },
    cta: { name: 'cta-night', position: '50% 55%' },
    cards: ['walkaround-dusk', 'hangar-night', 'throttle-dusk'],
  },
  day: {
    key: 'day',
    label: 'Day',
    swatch: ['#2f7cf6', '#ff5b1f'],
    themeColor: '#0a1930',
    hero: { name: 'hero-day', w: 3072, h: 1728, focus: [0.62, 0.6], target: [0.6, 0.75] },
    cta: { name: 'cta-day', position: '60% 50%' },
    cards: ['walkaround-day', 'hangar-day', 'throttle-day'],
  },
};

// 'auto' follows the visitor's sky (see lib/daylight); the others pin a look.
export const MODES = ['auto', 'dawn', 'night', 'day'];

const STORAGE_KEY = 'aq-mode';

export const imgUrl = (name, width) => `${process.env.PUBLIC_URL}/img/${name}-${width}.webp`;
export const srcSet = (name, widths) => widths.map((w) => `${imgUrl(name, w)} ${w}w`).join(', ');

// The style switcher is a mock-review tool: visible in development, or in
// production when the URL carries ?style=…
export const showStyleSwitcher = () =>
  process.env.NODE_ENV !== 'production' || new URLSearchParams(window.location.search).has('style');

// ?style=… wins for this visit; otherwise the last pick in the switcher.
function initialMode() {
  const param = new URLSearchParams(window.location.search).get('style');
  if (MODES.includes(param)) return param;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (MODES.includes(stored)) return stored;
  } catch (e) {
    // storage can be unavailable (private mode)
  }
  return 'auto';
}

export function useTheme() {
  const [mode, setMode] = useState(initialMode);
  // Decided once per visit, so the page never restyles while it's being read.
  const [phase] = useState(localPhase);
  const key = mode === 'auto' ? phase : mode;

  // Before paint, so the first frame already uses the right palette.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = key;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEMES[key].themeColor);
  }, [key]);

  const choose = useCallback((next) => {
    setMode(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // the choice still applies for this visit
    }
  }, []);

  return { theme: THEMES[key], mode, phase, setMode: choose };
}
